// src/components/LegalDisclaimer.tsx
import React, { useState } from 'react';
import './LegalDisclaimer.css';

export type DisclaimerType = 'pre-assessment' | 'post-assessment' | 'badge';

interface LegalDisclaimerProps {
  type: DisclaimerType;
  onAccept?: () => void;
}

export const LegalDisclaimer: React.FC<LegalDisclaimerProps> = ({ type, onAccept }) => {
  const [accepted, setAccepted] = useState(false);

  if (type === 'badge') {
    return (
      <div className="disclaimer-badge">
        <span className="badge-icon">ℹ️</span>
        <span className="badge-text">Educational tool only</span>
      </div>
    );
  }

  if (type === 'pre-assessment') {
    return (
      <div className="disclaimer-pre-assessment">
        <div className="disclaimer-box">
          <h2 className="disclaimer-title">Before You Start</h2>
          <div className="disclaimer-summary">
            <p className="summary-text">
              The HIPAA Navigator is an <strong>educational tool only</strong>. It will help you understand HIPAA compliance requirements, but is not a formal compliance audit or legal advice.
            </p>
          </div>
          <div className="disclaimer-points">
            <div className="disclaimer-point">
              <span className="point-icon">📋</span>
              <div className="point-content">
                <strong>Not an Audit</strong>
                <p>Results are based on your answers, not a professional assessment</p>
              </div>
            </div>
            <div className="disclaimer-point">
              <span className="point-icon">⚖️</span>
              <div className="point-content">
                <strong>Not Legal Advice</strong>
                <p>Consult with qualified legal professionals</p>
              </div>
            </div>
            <div className="disclaimer-point">
              <span className="point-icon">👨‍⚖️</span>
              <div className="point-content">
                <strong>Professional Review Needed</strong>
                <p>Hire compliance experts before implementing changes</p>
              </div>
            </div>
            <div className="disclaimer-point">
              <span className="point-icon">⚠️</span>
              <div className="point-content">
                <strong>Your Responsibility</strong>
                <p>You assume all risk for decisions based on this tool</p>
              </div>
            </div>
          </div>
          <div className="disclaimer-cta">
            <label className="disclaimer-checkbox">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>I understand this is an educational tool and will seek professional guidance</span>
            </label>
            <button className="btn-continue" onClick={onAccept} disabled={!accepted}>
              I Understand - Continue →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'post-assessment') {
    return (
      <div className="disclaimer-post-assessment">
        <div className="disclaimer-alert">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <h3 className="alert-title">Important: This Is Not A Compliance Audit</h3>
            <p className="alert-text">
              Your results are based on the answers you provided, not a professional evaluation. Before making compliance decisions:
            </p>
            <ol className="alert-checklist">
              <li>Engage a qualified HIPAA compliance consultant</li>
              <li>Conduct a formal security risk assessment (§164.308(a)(1)(ii)(A))</li>
              <li>Have legal counsel review your compliance program</li>
              <li>Work with compliance professionals on implementation</li>
            </ol>
            <p className="alert-note">
              <strong>Note:</strong> This tool provides general educational information only. Actual compliance requirements depend on your organization's specific circumstances. Consult professionals before taking action.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
